import React, { useMemo } from 'react';
import ReactEcharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import { timeAxisZoom, timeChartToolbox, ZOOMED_GRID_BOTTOM } from './timeZoom';

const CHANNEL_COLOURS = { X: '#5470c6', Y: '#91cc75', Z: '#fac858' };

export const GyroRecordChart = ({ gyroRecords, depthRecords, height = 600 }) => {
    const { t } = useTranslation();
    // Memoised so a parent re-render does not re-apply the option and throw
    // away the user's zoom (see timeZoom.js).
    const option = useMemo(() => {
        // Some tools only have a 2-axis gyro; the Z channel (Kz) is then reported as
        // a constant zero. Hide the Z series entirely when no record carries a
        // non-zero Z reading so the chart isn't cluttered with a flat-line series.
        const records = gyroRecords || [];
        const hasZ = records.some(record => record.Kz);

        // Auto-scale the Y axis to the signal, treating +/- 2000 counts as the
        // outlier threshold: scale to the data that falls within that window (and
        // round outward to a tidy 100) so a single large spike can't peg the axis
        // open and dwarf the trace. Anything beyond +/- 2000 is clipped by ECharts.
        const AXIS_CAP = 2000;
        const channelValues = [];
        records.forEach(record => {
            channelValues.push(record.Kx, record.Ky);
            if (hasZ) channelValues.push(record.Kz);
        });
        const inWindow = channelValues.filter(v => Number.isFinite(v) && v >= -AXIS_CAP && v <= AXIS_CAP);
        let yMin = -AXIS_CAP;
        let yMax = AXIS_CAP;
        if (inWindow.length) {
            const dataMin = inWindow.reduce((a, b) => Math.min(a, b), Infinity);
            const dataMax = inWindow.reduce((a, b) => Math.max(a, b), -Infinity);
            yMin = Math.max(-AXIS_CAP, Math.floor(dataMin / 100) * 100);
            yMax = Math.min(AXIS_CAP, Math.ceil(dataMax / 100) * 100);
            // Guard against a degenerate flat (all-equal) range.
            if (yMax <= yMin) {
                yMin -= 100;
                yMax += 100;
            }
        }

        // Build markLine data from depthRecords. If a Depth property exists include it in the label.
        const depthMarkLines = (depthRecords || []).map(record => {
            const label = record && record.Depth !== undefined ? `${record.Depth}` : '';
            return {
                xAxis: record.TimeStamp,
                name: label
            };
        });

        return {
            title: {
                text: t('charts.gyroRecordData'),
                left: 'center'
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross'
                }
            },
            toolbox: timeChartToolbox,
            dataZoom: timeAxisZoom(),
            grid: {
                bottom: ZOOMED_GRID_BOTTOM
            },
            xAxis: {
                type: 'time',
                scale: true,
                splitLine: {
                    show: true
                }
            },
            yAxis: {
                name: t('charts.values'),
                type: 'value',
                min: yMin,
                max: yMax,
                axisLabel: {
                    formatter: '{value}'
                },
                splitLine: {
                    show: true
                }
            },
            series: [
                // Colours are pinned per channel rather than left to the palette,
                // which assigns by series index: the Z series is conditional, so an
                // index-based palette would repaint X/Y differently depending on
                // whether the survey on screen has a Z channel.
                {
                    name: 'X',
                    type: 'line',
                    smooth: false,
                    itemStyle: { color: CHANNEL_COLOURS.X },
                    data: records.map(record => [record.TimeStamp, record.Kx]),
                },
                {
                    name: 'Y',
                    type: 'line',
                    smooth: false,
                    itemStyle: { color: CHANNEL_COLOURS.Y },
                    data: records.map(record => [record.TimeStamp, record.Ky]),
                },
                ...(hasZ ? [{
                    name: 'Z',
                    type: 'line',
                    smooth: false,
                    itemStyle: { color: CHANNEL_COLOURS.Z },
                    data: records.map(record => [record.TimeStamp, record.Kz]),
                }] : []),
                // Invisible helper series to render vertical markLines at depth timestamps
                {
                    name: 'DepthMarks',
                    type: 'line',
                    data: [], // no points, only markLine is used
                    tooltip: { show: false },
                    showSymbol: false,
                    hoverAnimation: false,
                    silent: true,
                    // Put mark lines above other layers
                    markLine: {
                        symbol: ['none', 'none'],
                        silent: true,
                        zlevel: 10,
                        label: {
                            // Use the provided name (Depth: ...) if present, show near the top of the line
                            formatter: params => params.name || '',
                            position: 'end',
                            color: '#333',
                            fontSize: 11
                        },
                        lineStyle: {
                            color: 'rgba(255, 0, 0, 0.7)',
                            type: 'dashed',
                            width: 1
                        },
                        data: depthMarkLines
                    }
                }
            ]
        };
    }, [gyroRecords, depthRecords, t]);

    return (
        <ReactEcharts
            option={option}
            // Replace the option outright instead of merging into the previous
            // survey's. ECharts merges by series index by default, so switching
            // to a survey with fewer series (no Z channel, or no records at all)
            // would leave the surplus series and its axis behind on the canvas.
            notMerge={true}
            style={{ height: height + 'px', width: '100%' }}
        />
    );
};

